import { RevenueDashboard } from './RevenueDashboard';
import { PayoutAmount } from '@evi/ui';
import { IdleReportCell } from '@/components/sessions/IdleReportCell';
import { DriverCell } from '@/components/sessions/DriverCell';
import { IdleFeesTab } from './IdleFeesTab';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3, DollarSign, Activity, Battery, ListChecks,
  Wallet, Truck, UsersRound, Zap,
} from 'lucide-react';
import {
  fetchOperatorSummary, fetchTopRevenueLocations, fetchLocations,
  fetchChargerUtilization, fetchChargerUptime, fetchSessionTrends,
  fetchSessions, formatCents, reportFilterSearchParams,
  fetchPayouts, payoutsCsvQuery, fetchSettlementSummary,
  fetchFleetBillingReport, fetchCustomerGroupsReport, fetchChargerDowntimeReport,
  type Location, type Session, type ReportFilterParams, type Payout,
  type FleetBillingRow, type CustomerGroupRow, type ChargerDowntimeRow,
} from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import { heldElectricityCents, heldNetCents, isHeldPayout } from '@/lib/heldPayout';
import { KpiCard, RevenueChart, RevenuePeriodPill, ThroughputChart, ExportActions } from '@evi/ui';
import { resolvePersonaId, formatReportMoney, reportCurrency } from '@evi/shared';
import { payoutPageTotals, payoutPageMoney, sessionPageAverage } from '@/lib/reportTotals';
import type { IdleFeeFilter, OperatorDashboardPeriod, TopRevenueRange } from '@evi/shared';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import {
  ScopedReportsPage,
  type PeriodOption,
  type TabSpec,
  type TabRenderState,
} from '@/components/reports/ScopedReportsPage';
import styles from './ReportsPage.module.css';

// ── Tab catalogue ───────────────────────────────────────────────────
//
// Eight reports total. Phase 1 ships the Revenue body; the other
// seven render a "Coming soon" stub via ComingSoonCard below. The
// catalogue order matches the discovery report's recommended
// presentation (revenue first because it's the highest-frequency
// operator question, uptime last because it's the most ops-y).

type ReportTab =
  | 'revenue'
  | 'utilization'
  | 'energy'
  | 'sessions'
  | 'idle-fees'
  | 'payouts'
  | 'fleet-billing'
  | 'groups'
  | 'uptime';

/** Operator-local tab catalogue entry. Carries a `description` for
 *  the Coming-soon stubs that the shell's persona-agnostic TabSpec
 *  doesn't model. Mapped to the shell's TabSpec via the operatorTabs
 *  closure in ReportsPage below. */
interface OperatorTabCatalogueEntry {
  id: ReportTab;
  label: string;
  icon: typeof BarChart3;
  /** One-liner shown in the Coming-soon stub. */
  description: string;
  /** Forwarded to the shell's TabSpec. A tab whose data needs a
   *  permission this viewer lacks is dropped from the bar and from the
   *  ?tab= allowlist — a fleet_admin lands on this same page and holds
   *  no Billing bit. */
  requiresPermission?: string;
}

const TABS: readonly OperatorTabCatalogueEntry[] = [
  { id: 'revenue',       label: 'Revenue',        icon: DollarSign, description: 'Total revenue by location, charger, and period.' },
  { id: 'utilization',   label: 'Utilization',    icon: Activity,   description: 'Sessions per charger per day, peak-hour heatmap.' },
  { id: 'energy',        label: 'Energy',         icon: Battery,    description: 'kWh delivered by location and charger.' },
  { id: 'sessions',      label: 'Sessions',       icon: ListChecks, description: 'Filterable session list with full session attributes.' },
  { id: 'idle-fees',     label: 'Idle fees',      icon: Wallet, description: 'Recorded idle fees and collection by location and currency.' },
  { id: 'payouts',       label: 'Payouts',        icon: Wallet,     description: 'Stripe payout reconciliation and settlement summary.', requiresPermission: 'billing:read' },
  { id: 'fleet-billing', label: 'Fleet Billing',  icon: Truck,      description: 'Per-fleet session counts and billing totals.', requiresPermission: 'fleet:read' },
  { id: 'groups',        label: 'Customer Groups',icon: UsersRound, description: 'Per-group activity and invoicing exports.', requiresPermission: 'group:read' },
  { id: 'uptime',        label: 'Uptime',         icon: Zap,        description: 'Charger uptime, fault counts, anomalies.' },
] as const;

// VALID_TABS / isReportTab removed in the ScopedReportsPage
// extraction — the shell handles URL state validation via the
// isValidTabId predicate it derives from the tabs[] array we pass.

// ── Period filter ───────────────────────────────────────────────────
//
// 5 pills, all live post reports-custom-range: operator-summary and
// top-revenue-locations accept 90d natively and arbitrary from/to
// custom ranges (365-day cap server-side), so the Phase 1 disabled
// scaffolding on 90d / Custom is gone. The URL state shape never
// changed, so pre-existing deep links keep working.
//
// '90d' carries days: 91, not 90 — the server's 90d window is 13
// whole weekly buckets (91 days, chosen so the chart never renders
// a partial final bucket). Any consumer of the shell's resolved
// from/to for this chip gets the same aligned window the chart and
// table cover.

type ReportPeriod = OperatorDashboardPeriod | 'custom';

const PERIOD_OPTIONS: ReadonlyArray<PeriodOption & { id: ReportPeriod }> = [
  { id: '24h',    label: '24h',    supported: true, kind: 'preset', days: 1 },
  { id: '7d',     label: '7d',     supported: true, kind: 'preset', days: 7 },
  { id: '30d',    label: '30d',    supported: true, kind: 'preset', days: 30 },
  { id: '90d',    label: '90d',    supported: true, kind: 'preset', days: 91 },
  { id: 'custom', label: 'Custom', supported: true, kind: 'custom' },
];

const VALID_PERIODS = new Set<ReportPeriod>(PERIOD_OPTIONS.map((p) => p.id));
function isReportPeriod(v: string): v is ReportPeriod {
  return VALID_PERIODS.has(v as ReportPeriod);
}

/** Real passthrough post reports-custom-range: every preset chip
 *  maps to itself ('90d' included — operator-summary accepts it
 *  natively). 'custom' has no ?period= equivalent; callers attach
 *  the shell-resolved from/to instead, which the server gives
 *  precedence over period, so the '7d' returned here is an inert
 *  placeholder that never shapes a custom response. */
function periodForApi(p: ReportPeriod): OperatorDashboardPeriod {
  return p === 'custom' ? '7d' : p;
}

/** Top-revenue-locations range passthrough. 7d / 30d / 90d map to
 *  themselves. 24h deliberately collapses to 7d — the endpoint has
 *  no 24h range, and a 24h-narrow rollup would show only ~1
 *  charger at most tenants anyway (the KPI tiles + chart still
 *  scope to 24h correctly via operator-summary). 'custom' rides
 *  the from/to params, which the server gives precedence, so the
 *  '7d' is the same inert placeholder periodForApi uses. */
function rangeForTopRevenue(p: ReportPeriod): TopRevenueRange {
  return p === '30d' || p === '90d' ? p : '7d';
}

// URL state hook, FilterBar, ReportTabs all removed in the
// ScopedReportsPage extraction (commit 3 of Building Admin Reports).
// They previously lived inline + duplicated with the SP page; now
// the shared shell at apps/admin/src/components/reports/
// ScopedReportsPage.tsx owns the chrome. Operator-specific
// composition (TABS array with tab-body closures, the centralized
// Sessions CSV in filterBarExtra) lives in the ReportsPage default
// export below.

// ── PeriodOption mapping ───────────────────────────────────────────
//
// The shell's PeriodOption uses `string` for id (persona-agnostic);
// the operator's ReportPeriod is a stricter local enum. Map once
// for the props handoff; the local enum still drives the tab-body
// data fetchers (periodForApi / rangeForTopRevenue / periodToFromTo
// all take ReportPeriod).
const PERIOD_OPTIONS_FOR_SHELL: ReadonlyArray<PeriodOption> = PERIOD_OPTIONS;

// ── ReportsPage ─────────────────────────────────────────────────────

export function ReportsPage() {
  useRecentRoute('Reports');
  const user = useAuthStore(s => s.user);
  const idleStaff = !user?.template && !user?.is_sales_agent
    && ['super_admin', 'operator', 'viewer'].includes(user?.role ?? '');
  const [idleChoice, setIdleChoice] = useState<IdleFeeFilter>('all');

  // Tenant locations list — drives the filter dropdown and the
  // SessionsTab's per-location name lookup.
  const locationsQ = useQuery({
    queryKey: ['admin', 'reports', 'locations'],
    queryFn: () => fetchLocations({ limit: 500, include_residential_reporting: true }),
    staleTime: 5 * 60_000,
  });
  const locations = locationsQ.data?.data ?? [];

  // Filter-dropdown variant: reporting-attached residential homes get
  // a distinguishing label so "Smith Home · Residential" reads apart
  // from a public site. The raw array keeps flowing to SessionsTab,
  // which applies its own display rule (name + city, never street).
  const dropdownLocations = useMemo(
    () => locations.map((l) => (
      l.type === 'residential' ? { ...l, name: `${l.name} · Residential` } : l
    )),
    [locations],
  );

  // Build the operator's tab inventory as TabSpec[]. Each tab's
  // render closure captures the persona-specific context (locations
  // for SessionsTab, ComingSoonCard for the not-yet-built tabs)
  // and pulls (period, locationId, onPeriodChange) from the shell's
  // tabRenderState at render time. Period is coerced through
  // isReportPeriod since the shell hands back a `string`; defensive
  // fallback to '7d' on unknown values matches the prior URL
  // validator behaviour.
  const operatorTabs: ReadonlyArray<TabSpec> = useMemo(() => TABS.filter(t => t.id !== 'idle-fees' || idleStaff).map((tabSpec) => ({
    id: tabSpec.id,
    label: tabSpec.label,
    icon: tabSpec.icon,
    ...(tabSpec.requiresPermission ? { requiresPermission: tabSpec.requiresPermission } : {}),
    render: ({ period, locationId, onPeriodChange, from, to, filters }) => {
      const reportPeriod: ReportPeriod = isReportPeriod(period) ? period : '7d';
      if (tabSpec.id === 'revenue') {
        // The legacy chart has a single USD series. Staff revenue uses the
        // recorded currency buckets so CAD and USD cannot form one total.
        if (idleStaff) return <RevenueDashboard from={from} to={to} filters={filters} />;
        return <RevenueTab
          period={reportPeriod}
          locationId={locationId}
          filters={filters}
          onPeriodChange={(next) => onPeriodChange(next)}
          from={from}
          to={to}
        />;
      }
      if (tabSpec.id === 'utilization') {
        return <UtilizationTab locationId={locationId} filters={filters} />;
      }
      if (tabSpec.id === 'energy') {
        return <EnergyTab
          period={reportPeriod}
          locationId={locationId}
          filters={filters}
          onPeriodChange={(next) => onPeriodChange(next)}
          from={from}
          to={to}
        />;
      }
      if (tabSpec.id === 'fleet-billing') {
        return <FleetBillingTab period={reportPeriod} filters={filters} from={from} to={to} />;
      }
      if (tabSpec.id === 'groups') {
        return <CustomerGroupsTab period={reportPeriod} filters={filters} from={from} to={to} />;
      }
      if (tabSpec.id === 'uptime') {
        return <UptimeTab period={reportPeriod} from={from} to={to} />;
      }
      if (tabSpec.id === 'payouts') {
        return <PayoutsTab
          period={reportPeriod}
          locationId={locationId}
          filters={filters}
          from={from}
          to={to}
        />;
      }
      if (tabSpec.id === 'sessions') {
        return <SessionsTab
          period={reportPeriod}
          locationId={locationId}
          filters={idleStaff ? { ...filters, idle_filter: idleChoice } : filters}
          locations={locations}
          from={from}
          to={to}
          idleSortable={idleStaff}
        />;
      }
      if (tabSpec.id === 'idle-fees') return <IdleFeesTab from={from} to={to} filters={{ ...filters, idle_filter: idleChoice }} />;
      return <ComingSoonCard label={tabSpec.label} description={tabSpec.description} />;
    },
  })), [locations, idleStaff, idleChoice]);

  // Centralized CSV export — operator's prior FilterBar hosted this
  // for the Sessions list regardless of active tab. Preserved via
  // the shell's filterBarExtra render-function slot so the existing
  // UX (download button always visible in the filter bar) survives
  // the extraction. The handler is the same one previously inlined
  // in FilterBar — only its home moved.
  const filterBarExtra = useCallback(
    ({ activeTabId, period, from, to, filters }: TabRenderState) => (
      <>
        {idleStaff && (activeTabId === 'sessions' || activeTabId === 'idle-fees') && <label className={styles.idleFilter}>
          Idle fee
          <select aria-label="Idle fee filter" value={idleChoice} onChange={e => setIdleChoice(e.target.value as IdleFeeFilter)}>
            <option value="all">All sessions</option><option value="any">Any idle fee</option>
            <option value="collected">Collected</option><option value="pending">Pending</option>
            <option value="waived">Waived or written off</option><option value="none">No idle fee</option>
          </select>
        </label>}
        <SessionsCsvButton period={period} filters={idleStaff && (activeTabId === 'sessions' || activeTabId === 'idle-fees')
          ? { ...filters, idle_filter: idleChoice } : filters} from={from} to={to} idleFees={activeTabId === 'idle-fees'} revenue={idleStaff && activeTabId === 'revenue'} />
      </>
    ),
    [idleStaff, idleChoice],
  );

  return (
    <ScopedReportsPage
      title="Reports"
      subtitle="Operational insights across your network"
      tabs={operatorTabs}
      defaultTabId="revenue"
      periodOptions={PERIOD_OPTIONS_FOR_SHELL}
      defaultPeriodId="7d"
      locations={dropdownLocations}
      locationFilter="always"
      chromeOrder="tabs-first"
      filterBarExtra={filterBarExtra}
      // Reporting v2 slice 1 — the Advanced filters control replaces
      // the single select; options derive from the same tagged list.
      advancedLocations={dropdownLocations}
    />
  );
}

// Centralized Sessions CSV button — extracted from the prior inline
// FilterBar (post-Commit-1) so the shell's filterBarExtra slot can
// render it without duplicating the busy-state machinery here.
// Owns local csvBusy state + the downloadAuthenticatedCsv call.
function SessionsCsvButton({
  period, filters, from, to, idleFees = false, revenue = false,
}: { period: string; filters: ReportFilterParams; from: string; to: string; idleFees?: boolean; revenue?: boolean }) {
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const reportPeriod: ReportPeriod = isReportPeriod(period) ? period : '7d';
      await downloadAuthenticatedCsv({
        path: revenue ? 'admin/reports/revenue/export.csv' : idleFees ? 'admin/reports/idle-fees/export.csv' : 'sessions/export',
        query: {
          // Reporting v2 slice 1 — the export carries the SAME filter
          // set the tab renders, serialized to the shared wire params.
          ...reportFilterSearchParams(filters),
          // Period → ISO date range. Every chip now exports exactly
          // the window the report shows: presets map to `now - Nd →
          // now` (91 days for the 90d chip, matching the chart and
          // table), custom passes the shell-resolved range through.
          // The old all-time/10K-cap fallback for 90d + custom is
          // gone with the un-gating.
          ...((idleFees || revenue) ? { from, to } : periodToFromTo(reportPeriod, { from, to })),
        },
        fallbackFilename: `evi-${revenue ? 'revenue' : idleFees ? 'idle-fees' : 'sessions'}-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, filters, period, from, to, idleFees, revenue]);

  return (
    <span className={styles.csvExport}>
      <ExportActions
        onCsv={handleDownloadCsv}
        csvBusy={csvBusy}
        pdfHref={null}
      />
      {csvError && <span className={styles.downloadError} role="alert">{csvError}</span>}
    </span>
  );
}

function periodToFromTo(
  period: ReportPeriod,
  resolved: { from: string; to: string },
): { from: string; to: string } {
  if (period === 'custom') {
    // Shell-resolved custom range (URL dates → ISO day boundaries;
    // falls back to a 7-day default while the user is still picking
    // dates — matching what the tab bodies display).
    return resolved;
  }
  // Presets: trailing windows from now. 90d exports 91 days — the
  // same 13-whole-week window the chart and table cover — so the
  // CSV rows sum to the totals on screen.
  const days = period === '24h' ? 1
    : period === '7d' ? 7
    : period === '30d' ? 30
    : 91;
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

// ── Revenue tab body ───────────────────────────────────────────────

function RevenueTab({
  period, locationId, filters, onPeriodChange, from, to,
}: {
  period: ReportPeriod;
  locationId: string | null;
  filters: ReportFilterParams;
  onPeriodChange: (next: ReportPeriod) => void;
  from: string;
  to: string;
}) {
  // Round-trip the chart's period-chip taps back through the URL
  // state so the filter-bar pills + chart's chips stay in lockstep
  // as a single source of truth. The chart emits only members of
  // its allowedPeriods list (24h/7d/30d) so the cast is safe.
  const onChartPeriodChange = (next: OperatorDashboardPeriod): void => {
    onPeriodChange(next);
  };

  // Custom ranges ride the shell-resolved from/to (which the server
  // gives precedence over ?period=); presets pass straight through.
  const isCustom = period === 'custom';
  const customRange = isCustom ? { from, to } : undefined;
  const apiPeriod = periodForApi(period);
  const topRange = rangeForTopRevenue(period);

  // Phase 2 — operator-summary now honors the location_ids filter
  // server-side (commit 7354e1b's TODO closed). Empty array =
  // tenant-wide aggregation, same as the dashboard path.
  const summaryQ = useQuery({
    queryKey: ['admin', 'reports', 'revenue', 'summary', isCustom ? `${from}..${to}` : apiPeriod, filters],
    queryFn: () => fetchOperatorSummary(apiPeriod, { filters, ...customRange }),
    // keepPreviousData: period switches shouldn't flash the KPI tiles
    // back to skeleton. Same pattern as LocationInfoCard Phase 3.
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
  const topQ = useQuery({
    queryKey: ['admin', 'reports', 'revenue', 'top', isCustom ? `${from}..${to}` : topRange, filters],
    queryFn: () => fetchTopRevenueLocations(topRange, { ...customRange, filters }),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });

  const summary = summaryQ.data;
  // KPI derivations from the available endpoint fields. Phase 1
  // limitations documented inline so future contributors don't
  // think the derivations are oversights:
  //   - Total revenue = sum of revenueLast24h buckets (USD)
  //   - Sessions    = sum of top-revenue-locations.session_count
  //                   (top 10 only — accurate at typical tenant
  //                   scale, becomes a "top 10 sessions" lower
  //                   bound past that)
  //   - Avg / session = total / sessions
  //   - Energy      = energyMtdKwh.value (MTD-scoped, not period-
  //                   scoped — labeled "MTD" to stay honest. Period-
  //                   scoped energy lands in Phase 2 via the Energy
  //                   tab + a server-side aggregation extension)
  const totalRevenueUsd = summary
    ? summary.revenueLast24h.reduce((acc, v) => acc + v, 0)
    : 0;
  const topRows = topQ.data?.rows ?? [];
  // Slice 1: the top-revenue endpoint filters server-side through the
  // shared compiler now, so the old client-side single-location
  // narrowing is gone — the rows already reflect the applied filters.
  const filteredRows = topRows;
  const sessionCount = filteredRows.reduce((acc, r) => acc + r.session_count, 0);
  const avgPerSession = sessionCount > 0
    ? totalRevenueUsd / sessionCount
    : 0;
  const energyMtdKwh = summary?.energyMtdKwh.value ?? 0;

  return (
    <div className={styles.revenueBody}>
      {/* KPI grid — 4 tiles, 2x2 on narrow / 4-up on wide per the
          existing dashboard pattern. */}
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total revenue"
          value={`$${totalRevenueUsd.toFixed(2)}`}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Sessions"
          value={sessionCount.toLocaleString()}
          meta={locationId ? 'selected location' : 'top 10 locations'}
        />
        <KpiCard
          label="Avg per session"
          value={sessionCount > 0 ? `$${avgPerSession.toFixed(2)}` : '—'}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Energy (MTD)"
          value={energyMtdKwh.toFixed(1)}
          unit="kWh"
          meta="month-to-date"
        />
      </div>

      {/* Revenue chart — same component the dashboard uses. The
          in-chart chip row stays the three quick presets; for 90d /
          custom selections we pass '90d' (not in allowedPeriods) so
          NO chart chip renders active — the filter pills above carry
          the real state, and highlighting 7d while showing a custom
          range would lie. Documented RevenueChart behavior: an
          off-list period renders with the active chip invisible. */}
      <div className={styles.chartCard}>
        <RevenueChart
          data={summary?.revenueLast24h ?? []}
          period={period === '24h' || period === '7d' || period === '30d' ? period : '90d'}
          onPeriodChange={onChartPeriodChange}
          allowedPeriods={['24h', '7d', '30d']}
          // currentRate is required by the component signature but
          // ignored when `footer` is provided (the footer prop wins
          // and replaces the default RATE: CURRENT row). Pass 0 to
          // satisfy the type.
          currentRate={0}
          showRate={false}
          footer={
            <RevenuePeriodPill
              label="TOTAL"
              amount={totalRevenueUsd}
              sessionCount={sessionCount}
            />
          }
        />
      </div>

      {/* Breakdown table — top revenue locations. Sort header
          handlers are client-side; top 10 rows keeps the sort cheap. */}
      <RevenueByLocationTable
        rows={filteredRows}
        loading={topQ.isLoading}
      />
    </div>
  );
}

function describePeriod(p: ReportPeriod): string {
  switch (p) {
    case '24h': return 'last 24 hours';
    case '7d':  return 'last 7 days';
    case '30d': return 'last 30 days';
    case '90d': return 'last 90 days';
    case 'custom': return 'custom range';
  }
}

// ── Revenue-by-location table ──────────────────────────────────────
//
// Phase 3C — consumes ResponsiveTable's new sortable-column primitive
// rather than the inline sort implementation Phase 1 shipped. Same
// visible behaviour (three columns, click to sort, accent arrow on
// active column) but the sort machinery is now reusable across other
// admin tables that opt in.

type TopRow = {
  location_id: string;
  location_name: string;
  revenue_cents: number;
  session_count: number;
};

function RevenueByLocationTable({
  rows, loading,
}: { rows: TopRow[]; loading: boolean }) {
  const columns: Column<TopRow>[] = [
    {
      key: 'location_name',
      header: 'Location',
      cell: (r) => r.location_name,
      sortable: true,
      sortKey: (r) => r.location_name,
      mobile: 'primary',
    },
    {
      key: 'session_count',
      header: 'Sessions',
      cell: (r) => r.session_count.toLocaleString(),
      sortable: true,
      sortKey: (r) => r.session_count,
      align: 'right',
      mobile: 'body',
    },
    {
      key: 'revenue_cents',
      header: 'Revenue ($)',
      cell: (r) => `$${(r.revenue_cents / 100).toFixed(2)}`,
      sortable: true,
      sortKey: (r) => r.revenue_cents,
      align: 'right',
      mobile: 'body',
    },
  ];

  return (
    <div className={styles.tableCard}>
      <h3 className={styles.tableTitle}>Revenue by Location</h3>
      {loading && rows.length === 0 ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.location_id}
          emptyMessage="No revenue data for the selected period."
          initialSortKey="revenue_cents"
          initialSortDir="desc"
        />
      )}
    </div>
  );
}

function SortHeader({
  label, active, dir, onClick, align = 'left',
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <th style={{ textAlign: align }}>
      <button
        type="button"
        className={styles.sortBtn}
        onClick={onClick}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {label}
        {active ? <span className={styles.sortArrow}>{dir === 'asc' ? '↑' : '↓'}</span> : null}
      </button>
    </th>
  );
}

// ── Utilization tab body (Phase 2) ─────────────────────────────────
//
// Composition over three existing dashboard endpoints:
//   - charger-utilization (per-charger 7d session-based util%)
//   - charger-uptime      (network online% now + 24h sparkline)
//   - session-trends      (30 daily buckets of session_count +
//                          avg_duration_minutes + avg_kwh)
//
// Data-source gaps from the prompt's spec, flagged in commit body:
//   - "Faulted Time %": no endpoint exposes faulted time. Substituted
//     with Avg Session Duration (session-trends.avg_duration_minutes_7d)
//     which is the closest available signal.
//   - "Peak Concurrent Sessions": not tracked. Substituted with Avg
//     kWh per Session (session-trends.avg_kwh_7d).
// Both substitutions land in the same "operations health" bucket; a
// future server-side aggregation could backfill the originals.
//
// Period filter is informational here: charger-utilization is a fixed
// 7d window; charger-uptime is a 24h sparkline; session-trends is 30d.
// Tabs whose data sources don't honor the filter bar's period chips
// render the chip row but the data shown is the endpoint's native
// window. Phase 3 will add server-side window params.

function UtilizationTab({
  filters,
}: { locationId: string | null; filters: ReportFilterParams }) {
  // Slice 1: all three endpoints filter server-side through the
  // shared compiler now (the old client-side no-op narrowing and its
  // Phase 3 flag are retired). Filters ride the query keys so a
  // change refetches.
  const utilQ = useQuery({
    queryKey: ['admin', 'reports', 'utilization', 'rows', filters],
    queryFn: () => fetchChargerUtilization(filters),
    staleTime: 60_000,
  });
  const uptimeQ = useQuery({
    queryKey: ['admin', 'reports', 'utilization', 'uptime', filters],
    queryFn: () => fetchChargerUptime(filters),
    staleTime: 60_000,
  });
  const trendsQ = useQuery({
    queryKey: ['admin', 'reports', 'utilization', 'trends', filters],
    queryFn: () => fetchSessionTrends(filters),
    staleTime: 60_000,
  });

  const utilRows = utilQ.data?.rows ?? [];

  // KPI derivations
  // ───────────────
  // Avg sessions/charger/day = (sum session_count) / (chargers) / 7
  // (charger-utilization is a fixed 7-day window; the formula
  //  reflects the actual data shape rather than the chip period.)
  const totalSessions = utilRows.reduce((acc, r) => acc + r.session_count, 0);
  const chargerCount = utilRows.length;
  const avgSessionsPerChargerPerDay = chargerCount > 0
    ? totalSessions / chargerCount / 7
    : 0;
  const networkUptime = uptimeQ.data?.percent_online_now ?? 0;
  const avgDuration = trendsQ.data?.avg_duration_minutes_7d ?? null;
  const avgKwh = trendsQ.data?.avg_kwh_7d ?? null;

  // Throughput chart bucket build — session-trends.daily_buckets is
  // 30 daily entries. ThroughputChart wants { sessions, kwh } per
  // bucket; we derive kwh = session_count * avg_kwh per day.
  const throughputData = (trendsQ.data?.daily_buckets ?? []).map((b) => ({
    sessions: b.session_count,
    kwh: b.avg_kwh != null ? b.session_count * b.avg_kwh : 0,
  }));

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Avg sessions / charger / day"
          value={avgSessionsPerChargerPerDay.toFixed(2)}
          meta="last 7 days"
        />
        <KpiCard
          label="Network uptime"
          value={`${networkUptime.toFixed(1)}%`}
          meta={uptimeQ.data ? `${uptimeQ.data.total_chargers} chargers in scope` : 'now'}
        />
        <KpiCard
          label="Avg session duration"
          value={avgDuration != null ? `${avgDuration.toFixed(0)}m` : '—'}
          meta="last 7 days"
        />
        <KpiCard
          label="Avg kWh / session"
          value={avgKwh != null ? avgKwh.toFixed(1) : '—'}
          unit="kWh"
          meta="last 7 days"
        />
      </div>

      <div className={styles.chartCard}>
        <ThroughputChart
          data={throughputData}
          period="30d"
          onPeriodChange={() => {
            // session-trends is fixed-30d today; period chip taps are
            // a no-op until Phase 3 wires server-side windowing.
          }}
          allowedPeriods={['30d']}
        />
      </div>

      <UtilizationByChargerTable
        rows={utilRows}
        loading={utilQ.isLoading}
      />
    </div>
  );
}

type UtilRow = {
  charger_id: string;
  charger_identity: string;
  location_name: string | null;
  utilization_percent: number;
  session_count: number;
  total_active_minutes: number;
};
type UtilSortKey = 'charger_identity' | 'location_name' | 'session_count' | 'utilization_percent' | 'avg_duration';

function UtilizationByChargerTable({
  rows, loading,
}: { rows: UtilRow[]; loading: boolean }) {
  const [sortKey, setSortKey] = useState<UtilSortKey>('utilization_percent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const withDerived = rows.map((r) => ({
      ...r,
      avg_duration: r.session_count > 0 ? r.total_active_minutes / r.session_count : 0,
    }));
    withDerived.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return withDerived.slice(0, 10);
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key: UtilSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'charger_identity' || key === 'location_name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className={styles.tableCard}>
      <h3 className={styles.tableTitle}>Top 10 by Utilization</h3>
      {loading && rows.length === 0 ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className={styles.emptyState}>No utilization data for the selected period.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <SortHeader label="Charger" active={sortKey === 'charger_identity'} dir={sortDir} onClick={() => toggleSort('charger_identity')} />
              <SortHeader label="Location" active={sortKey === 'location_name'} dir={sortDir} onClick={() => toggleSort('location_name')} />
              <SortHeader label="Sessions" active={sortKey === 'session_count'} dir={sortDir} onClick={() => toggleSort('session_count')} align="right" />
              <SortHeader label="Avg duration" active={sortKey === 'avg_duration'} dir={sortDir} onClick={() => toggleSort('avg_duration')} align="right" />
              <SortHeader label="Utilization %" active={sortKey === 'utilization_percent'} dir={sortDir} onClick={() => toggleSort('utilization_percent')} align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.charger_id}>
                <td><code style={{ fontSize: 12 }}>{r.charger_identity}</code></td>
                <td>{r.location_name ?? '—'}</td>
                <td className={styles.numCell}>{r.session_count.toLocaleString()}</td>
                <td className={styles.numCell}>{r.avg_duration.toFixed(0)}m</td>
                <td className={styles.numCell}>{r.utilization_percent.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Energy tab body (Phase 2) ──────────────────────────────────────
//
// Same operator-summary source as the Revenue tab, but reads the
// energyLast24h series (added in Phase 2C alongside revenueLast24h)
// and renders it through the SAME RevenueChart component via the
// new titleLabel + formatY props — one chart component, two
// dimensions.
//
// CO2 estimate
// ────────────
// Phase 2 ships a FLAT coefficient of 0.4 kg CO2/kWh, matching the
// North American grid average per EPA eGRID. Region-specific
// coefficients (per-tenant lookup by Location address) is the
// discovery report's open question 5 — filed as Phase 5 work
// alongside the carbon-credits ESG report. The current estimate is
// honest as a national-average estimate; the tile label says
// "estimated" to underscore the approximation.

const CO2_COEFFICIENT_KG_PER_KWH = 0.4;

function EnergyTab({
  period, filters, onPeriodChange, from, to,
}: {
  period: ReportPeriod;
  locationId: string | null;
  filters: ReportFilterParams;
  onPeriodChange: (next: ReportPeriod) => void;
  from: string;
  to: string;
}) {
  // Custom ranges ride the shell-resolved from/to (server gives them
  // precedence over ?period=); presets pass straight through.
  const isCustom = period === 'custom';
  const customRange = isCustom ? { from, to } : undefined;
  const apiPeriod = periodForApi(period);

  const summaryQ = useQuery({
    queryKey: ['admin', 'reports', 'energy', 'summary', isCustom ? `${from}..${to}` : apiPeriod, filters],
    queryFn: () => fetchOperatorSummary(apiPeriod, { filters, ...customRange }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
  // Per-location energy breakdown — reuses top-revenue-locations
  // (which surfaces session_count by location). Energy per location
  // needs a server-side aggregation; for v1 we derive avg
  // energy/session from the period-wide totals and multiply per
  // location's session_count. Approximation acceptable for top-10
  // ranking display.
  const topQ = useQuery({
    queryKey: ['admin', 'reports', 'energy', 'top', isCustom ? `${from}..${to}` : rangeForTopRevenue(period), filters],
    queryFn: () => fetchTopRevenueLocations(rangeForTopRevenue(period), { ...customRange, filters }),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });

  const summary = summaryQ.data;
  const energyBuckets = summary?.energyLast24h ?? [];
  const totalKwh = energyBuckets.reduce((acc, v) => acc + v, 0);
  const topRows = topQ.data?.rows ?? [];
  // Slice 1: server-side filtering replaces the old client narrow.
  const filteredRows = topRows;
  const totalSessions = filteredRows.reduce((acc, r) => acc + r.session_count, 0);
  const avgKwhPerSession = totalSessions > 0 ? totalKwh / totalSessions : 0;
  const co2Kg = totalKwh * CO2_COEFFICIENT_KG_PER_KWH;
  const networkSize = summary?.network.total ?? 0;
  // Window length for the energy/charger/day divisor. 90d divides
  // by 91 (the chart's whole-week window); custom derives from the
  // shell-resolved range, floored at 1 day.
  const days = period === '24h' ? 1
    : period === '7d' ? 7
    : period === '30d' ? 30
    : period === '90d' ? 91
    : Math.max(1, (new Date(to).getTime() - new Date(from).getTime()) / (24 * 60 * 60 * 1000));
  const energyPerChargerPerDay = networkSize > 0 ? totalKwh / networkSize / days : 0;

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total energy"
          value={totalKwh.toFixed(1)}
          unit="kWh"
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Avg per session"
          value={totalSessions > 0 ? avgKwhPerSession.toFixed(1) : '—'}
          unit="kWh"
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Estimated CO₂ offset"
          value={co2Kg.toFixed(0)}
          unit="kg"
          meta="0.4 kg / kWh · NA grid avg"
        />
        <KpiCard
          label="Energy / charger / day"
          value={energyPerChargerPerDay.toFixed(1)}
          unit="kWh"
          meta={`${networkSize} chargers in scope`}
        />
      </div>

      {/* Same off-list trick as RevenueTab: 90d / custom pass '90d'
          (not in allowedPeriods) so no chart chip renders falsely
          active — the filter pills above carry the real state. */}
      <div className={styles.chartCard}>
        <RevenueChart
          data={energyBuckets}
          period={period === '24h' || period === '7d' || period === '30d' ? period : '90d'}
          onPeriodChange={onPeriodChange}
          allowedPeriods={['24h', '7d', '30d']}
          // Energy variant of the chart — same component, swapped
          // title pill + Y-axis formatter so kWh renders where
          // dollars usually go. Phase 2C extension on RevenueChart.
          titleLabel="ENERGY: kWh"
          // The all-zero empty state reads "No energy in the ...", not
          // the component's default "No revenue in the ...".
          emptyNoun="energy"
          formatY={(n) => `${Math.round(n)} kWh`}
          currentRate={0}
          showRate={false}
          footer={
            <RevenuePeriodPill
              label="TOTAL"
              amount={totalKwh}
              sessionCount={totalSessions}
            />
          }
        />
      </div>

      <EnergyByLocationTable
        rows={filteredRows}
        totalKwh={totalKwh}
        totalSessions={totalSessions}
        loading={topQ.isLoading}
      />
    </div>
  );
}

type EnergyRow = {
  location_id: string;
  location_name: string;
  session_count: number;
  estimated_kwh: number;
};

function EnergyByLocationTable({
  rows, totalKwh, totalSessions, loading,
}: {
  rows: TopRow[];
  totalKwh: number;
  totalSessions: number;
  loading: boolean;
}) {
  // Per-location energy estimate: until a server-side energy-by-
  // location endpoint ships, derive from period-wide avg
  // kWh/session × this location's session_count. Approximation —
  // the actual per-location number could diverge if one location's
  // chargers are higher-capacity. Flagged inline; Phase 3 server
  // endpoint would replace the derivation.
  const avgKwh = totalSessions > 0 ? totalKwh / totalSessions : 0;
  const energyRows: EnergyRow[] = rows.map((r) => ({
    location_id: r.location_id,
    location_name: r.location_name,
    session_count: r.session_count,
    estimated_kwh: r.session_count * avgKwh,
  }));

  type Key = 'location_name' | 'session_count' | 'estimated_kwh';
  const [sortKey, setSortKey] = useState<Key>('estimated_kwh');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const out = [...energyRows];
    out.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return out;
  }, [energyRows, sortKey, sortDir]);

  const toggleSort = (k: Key) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'location_name' ? 'asc' : 'desc'); }
  };

  return (
    <div className={styles.tableCard}>
      <h3 className={styles.tableTitle}>Energy by Location (estimated)</h3>
      {loading && energyRows.length === 0 ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : energyRows.length === 0 ? (
        <p className={styles.emptyState}>No energy data for the selected period.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <SortHeader label="Location" active={sortKey === 'location_name'} dir={sortDir} onClick={() => toggleSort('location_name')} />
              <SortHeader label="Sessions" active={sortKey === 'session_count'} dir={sortDir} onClick={() => toggleSort('session_count')} align="right" />
              <SortHeader label="Est. energy (kWh)" active={sortKey === 'estimated_kwh'} dir={sortDir} onClick={() => toggleSort('estimated_kwh')} align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.location_id}>
                <td>{r.location_name}</td>
                <td className={styles.numCell}>{r.session_count.toLocaleString()}</td>
                <td className={styles.numCell}>{r.estimated_kwh.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Sessions tab body (Phase 2) ────────────────────────────────────
//
// Filterable + paginated session list — the highest-utility report
// for tax/accounting per the Phase 1 discovery. Reuses the existing
// `/sessions` endpoint (already supports location_id + from + to +
// status filters + page/limit pagination); the bulk-export path
// is the same Export CSV anchor in the filter bar above.
//
// Data-source gaps (flagged):
//   - "Stacked bar: completed vs failed per day": session-trends
//     emits a session_count per day but doesn't split by status.
//     Phase 3 server extension would add the status split; v1
//     skips the chart and ships KPIs + table only.
//   - "Most common end-reason": Session.status is an enum, not a
//     fine-grained end-reason. Substituted with the
//     completed-percentage tile (% of sessions in the page that
//     completed cleanly).
//   - Hydrated names: /sessions returns charger_id + location_id
//     raw, not charger_identity + location_name. The table joins
//     client-side against the locations list passed in from the
//     parent for location names; charger ids stay raw (truncated)
//     until a server hydration ships in Phase 3.

const PAGE_SIZE = 25;

function SessionsTab({
  period, locationId, filters, locations, from, to, idleSortable,
}: {
  period: ReportPeriod;
  locationId: string | null;
  filters: ReportFilterParams;
  locations: Location[];
  from: string;
  to: string;
  idleSortable: boolean;
}) {
  const [page, setPage] = useState(1);
  const [idleSort, setIdleSort] = useState<'asc' | 'desc' | undefined>();

  // Reset page on filter change so a stale page= doesn't strand
  // the user on an empty page. from/to in the deps so picking new
  // custom dates also resets.
  useEffect(() => { setPage(1); }, [period, locationId, JSON.stringify(filters), from, to]);

  // /sessions accepts arbitrary from/to (the same range machinery
  // sessions/export uses), so every chip — 90d and custom included —
  // maps to a real date filter. The old "future phase" gate is gone
  // with the chip un-gating.
  const range = periodToFromTo(period, { from, to });
  const sessionsQ = useQuery({
    queryKey: ['admin', 'reports', 'sessions', period === 'custom' ? `${from}..${to}` : period, filters, page, idleSort],
    queryFn: () => fetchSessions({
      page,
      limit: PAGE_SIZE,
      // Slice 1: the shared filter set covers location narrowing (the
      // legacy single pick folds in as a one-element location_ids).
      filters,
      from: range.from,
      to: range.to,
      ...(idleSort ? { sort_by: 'idle_fee' as const, sort_order: idleSort } : {}),
    }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const sessions = sessionsQ.data?.data ?? [];
  const total = sessionsQ.data?.meta.total ?? 0;
  const totalPages = sessionsQ.data?.meta.pages ?? 1;

  // KPI derivations from the current page (full-window numbers would
  // need a separate aggregation endpoint; this is the v1 fit).
  const completed = sessions.filter((s) => s.status === 'completed' || s.status === 'stopped_by_user' || s.status === 'stopped_by_system').length;
  const failed = sessions.filter((s) => s.status === 'failed').length;
  const avgDurationMin = sessions.length > 0
    ? sessions.reduce((acc, s) => acc + (s.duration_minutes ?? 0), 0) / sessions.length
    : 0;
  const avgCost = sessionPageAverage(sessions);

  // Persona gate for the Who column — same pattern as the Booking
  // History Tenant column's visibleFor: resolve the persona from the
  // auth store and show driver identity to admin personas only.
  // driver_name is server-hydrated (name with email fallback).
  const user = useAuthStore((s) => s.user);
  const persona = resolvePersonaId({
    role: user?.role,
    is_sales_agent: user?.is_sales_agent ?? false,
  });
  const showWho = persona === 'super_admin' || persona === 'tenant_admin' || persona === 'operator';

  // Display labels: residential homes render the owner-set name plus
  // city — never the street address — on operator report surfaces.
  const locationNameById = new Map(locations.map((l) => [
    l._id,
    l.type === 'residential'
      ? `${l.name}${l.address.city ? ` · ${l.address.city}` : ''}`
      : l.name,
  ] as const));

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total sessions"
          value={total.toLocaleString()}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Completed / failed"
          value={
            <span>
              {completed.toLocaleString()}
              <span style={{ color: 'var(--color-tx-m)', fontWeight: 400, margin: '0 6px' }}>·</span>
              {failed.toLocaleString()}
            </span>
          }
          meta="this page"
        />
        <KpiCard
          label="Avg duration"
          value={`${avgDurationMin.toFixed(0)}m`}
          meta="this page"
        />
        <KpiCard
          label="Avg cost"
          value={avgCost}
          meta="this page"
        />
      </div>

      <SessionsTable
        sessions={sessions}
        locationNameById={locationNameById}
        showWho={showWho}
        loading={sessionsQ.isLoading}
        page={page}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
        onIdleSort={idleSortable ? direction => { setIdleSort(direction); setPage(1); } : undefined}
      />
    </div>
  );
}

const STATUS_LABELS: Record<Session['status'], string> = {
  active: 'Active',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (user)',
  stopped_by_system: 'Stopped (system)',
};

function SessionsTable({
  sessions, locationNameById, showWho, loading, page, totalPages, total, onPageChange, onIdleSort,
}: {
  sessions: Session[];
  locationNameById: Map<string, string>;
  showWho: boolean;
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (next: number) => void;
  onIdleSort?: (direction: 'asc' | 'desc') => void;
}) {
  return (
    <div className={styles.tableCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h3 className={styles.tableTitle} style={{ marginBottom: 0 }}>Sessions</h3>
        <span style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>
          {total > 0 ? `${total.toLocaleString()} total` : ''}
        </span>
      </div>
      {loading && sessions.length === 0 ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : sessions.length === 0 ? (
        <p className={styles.emptyState}>No sessions for the selected filter.</p>
      ) : (
        <>
          <ResponsiveTable<Session>
            mobileSort={!!onIdleSort}
            serverSorted={!!onIdleSort}
            rows={sessions}
            columns={[
              { key: 'started', header: 'Started', mobile: 'primary', cell: s => new Date(s.started_at).toLocaleString() },
              { key: 'status', header: 'Status', mobile: 'secondary', cell: s => STATUS_LABELS[s.status] },
              { key: 'location', header: 'Location', cell: s => locationNameById.get(s.location_id) ?? 'Not recorded' },
              ...(showWho ? [{ key: 'who', header: 'Who', cell: (s: Session) => <DriverCell row={s} /> }] : []),
              { key: 'duration', header: 'Duration', cell: s => s.duration_minutes ? Math.round(s.duration_minutes) + ' min' : 'Not recorded' },
              { key: 'energy', header: 'Energy', cell: s => s.energy_kwh.toFixed(1) + ' kWh' },
              { key: 'cost', header: 'Cost', cell: s => formatReportMoney(s.cost?.total ?? 0, s.cost?.currency) },
              { key: 'idle', header: 'Idle fee and collection', sortable: !!onIdleSort,
                sortKey: s => (s.idle_report?.currency ?? '') + ':' + String(s.idle_report?.idle_fee_cents ?? 0).padStart(18, '0'),
                cell: s => <IdleReportCell report={s.idle_report} /> },
            ]}
            rowKey={s => s._id}
            onSortChange={(key, direction) => { if (key === 'idle') onIdleSort?.(direction); }}
          />

          {totalPages > 1 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginTop: 12, fontSize: 12,
            }}>
              <span style={{ color: 'var(--color-tx-m)' }}>
                Page {page} of {totalPages}
              </span>
              <div style={{ display: 'inline-flex', gap: 6 }}>
                <button
                  type="button"
                  className={styles.exportBtn}
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  style={{ opacity: page <= 1 ? 0.5 : 1 }}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className={styles.exportBtn}
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  style={{ opacity: page >= totalPages ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Payouts tab ────────────────────────────────────────────────────
//
// "Stripe payout reconciliation and settlement summary" — the per-session
// payout ledger for the selected period, under two settlement tiles.
//
// Deliberately NOT the Revenue tab again. Revenue aggregates the
// gross→owner-payout waterfall; this is the row-level ledger those
// totals come from, which is what you open when a number needs
// explaining or a transfer id needs finding.
//
// ⚠️ THE TWO TILES ARE NOT PERIOD-FILTERED, and they say so on their
// face. /dashboard/settlement-summary and /dashboard/pending-payouts
// have no date range by design — the first is lifetime-unsettled, the
// second is whatever Stripe currently has in flight to the bank. Left
// unlabelled under a 7d chip they would read as "7d", which is the
// failure this whole pass has been removing. The table below them IS
// period-filtered, and that asymmetry is stated rather than hidden.

function payoutMoney(cents: number, currency: unknown): string {
  const code = reportCurrency(currency);
  return code ? formatCents(cents, code) : 'Currency not recorded';
}

function PayoutsTab({
  period, locationId, filters, from, to,
}: {
  period: ReportPeriod;
  locationId: string | null;
  filters: ReportFilterParams;
  from: string;
  to: string;
}) {
  const [page, setPage] = useState(1);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Same reset rule as SessionsTab: a stale page= must not strand the
  // reader on an empty page when the filter set changes underneath.
  useEffect(() => { setPage(1); }, [period, locationId, JSON.stringify(filters), from, to]);

  const range = periodToFromTo(period, { from, to });

  const payoutsQ = useQuery({
    queryKey: ['admin', 'reports', 'payouts', period === 'custom' ? `${from}..${to}` : period, filters, page],
    queryFn: () => fetchPayouts(
      { page, limit: PAGE_SIZE, from: range.from, to: range.to },
      filters,
    ),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  // Period-less by construction — see the header note.
  const settlementQ = useQuery({
    queryKey: ['admin', 'reports', 'settlement-summary'],
    queryFn: fetchSettlementSummary,
    staleTime: 60_000,
  });

  const payouts = payoutsQ.data?.data ?? [];
  const total = payoutsQ.data?.meta.total ?? 0;
  const totalPages = payoutsQ.data?.meta.pages ?? 1;

  // Page-scoped sums, labelled as such. A window-wide total would need
  // its own aggregation; /billing/revenue already answers that question
  // and is the Revenue tab.
  const pageMoney = payoutPageTotals(payouts);

  const onExport = async (): Promise<void> => {
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      // downloadAuthenticatedCsv, NOT exportPayoutsCsvUrl: the auth
      // plugin reads only the Bearer header, and a top-level <a href
      // download> navigation cannot carry it, so the old link 401s.
      await downloadAuthenticatedCsv({
        path: 'billing/payouts',
        query: payoutsCsvQuery({ from: range.from, to: range.to }, filters),
        fallbackFilename: `payouts-${today}.csv`,
      });
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setCsvBusy(false);
    }
  };

  const columns: Column<Payout>[] = [
    { key: 'created_at', header: 'Date', mobile: 'primary', cell: (p) => (p.created_at ? new Date(p.created_at).toLocaleDateString() : '—') },
    // Collect and hold (@/lib/heldPayout): a held row's breakdown records
    // $0 electricity and the whole charge as platform fee until a human
    // pays the host and Site. The Status cell marks it, Electricity shows
    // the Site's owed amount, Platform fee what EVI actually holds (the
    // charge less Stripe's fee), so the row never reads "EVI took it all".
    {
      key: 'status',
      header: 'Status',
      cell: (p) => (isHeldPayout(p)
        ? (
          <>
            {p.status}
            <span className={styles.heldTag} title="Site electricity exceeded the session's energy revenue: the charge is held on EVI's balance until the host and Site are paid manually.">
              Held
            </span>
          </>
        )
        : p.status),
    },
    { key: 'energy', header: 'kWh', mobile: 'hidden', cell: (p) => (p.breakdown?.energy_kwh ?? 0).toFixed(2) },
    { key: 'gross', header: 'Gross', cell: (p) => payoutMoney(p.breakdown?.gross_amount_cents ?? 0, p.breakdown.currency) },
    { key: 'idle', header: 'Idle subtotal', mobile: 'body', cell: (p) => <strong>{formatReportMoney(p.idle_subtotal_cents, p.breakdown.currency)}</strong> },
    { key: 'stripe', header: 'Stripe fee', mobile: 'hidden', cell: (p) => payoutMoney(p.breakdown?.stripe_fee_cents ?? 0, p.breakdown.currency) },
    {
      key: 'electricity',
      header: 'Electricity',
      mobile: 'hidden',
      cell: (p) => {
        const heldElectricity = heldElectricityCents(p);
        return heldElectricity !== null
          ? `${payoutMoney(heldElectricity, p.breakdown.currency)} held, paid manually`
          : payoutMoney(p.breakdown?.electricity_cost_cents ?? 0, p.breakdown.currency);
      },
    },
    {
      key: 'platform',
      header: 'Platform fee',
      mobile: 'hidden',
      cell: (p) => {
        const heldNet = heldNetCents(p);
        return heldNet !== null
          ? `${payoutMoney(heldNet, p.breakdown.currency)} held, not EVI's fee`
          : payoutMoney(p.breakdown?.platform_fee_cents ?? 0, p.breakdown.currency);
      },
    },
    { key: 'owner', header: 'Owner payout', cell: (p) => <PayoutAmount cents={p.breakdown?.owner_payout_cents ?? 0} currency={p.breakdown.currency} cash={p.host_cash_by_currency} locale={navigator.language} /> },
  ];

  const settlement = settlementQ.data;

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        {/* Held electricity (collect and hold) is owed too but pending,
            paid manually: named beside the total, never added to it. */}
        <KpiCard
          label="Unsettled to date"
          value={(settlement?.money_by_currency ?? []).map(b => formatReportMoney(b.total_owed_cents, b.currency)).join(' · ') || 'No unsettled payments'}
          meta={[
            settlement?.session_count
              ? `${settlement.session_count} sessions · all time, not this period`
              : 'All time, not this period',
            (settlement?.held_session_count ?? 0) > 0
              ? (settlement?.money_by_currency ?? []).filter(b => b.held_session_count > 0).map(b => `${formatReportMoney(b.held_electricity_cents, b.currency)} held, paid manually`).join(' · ')
              : null,
          ].filter(Boolean).join(' · ')}
        />
        <KpiCard
          label="Oldest unsettled"
          value={settlement?.oldest_session_date
            ? new Date(settlement.oldest_session_date).toLocaleDateString()
            : '—'}
          meta="All time, not this period"
        />
        <KpiCard
          label="Gross on this page"
          value={payoutPageMoney(pageMoney, 'gross_cents')}
          meta={`${payouts.length} of ${total} rows`}
        />
        <KpiCard
          label="Owner payout on this page"
          value={payoutPageMoney(pageMoney, 'owner_cents')}
          meta={`${payouts.length} of ${total} rows`}
        />
      </div>

      {csvError && <p className={styles.emptyState}>{csvError}</p>}

      <div className={styles.tableCard}>
        {/* Title + export on one row, using the classes this stylesheet
            actually defines — .tableTitle for the heading and
            .exportActions for the button cluster, the same pair the
            Revenue and Utilization tables use. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 className={styles.tableTitle}>Payout ledger</h3>
          <div className={styles.exportActions}>
            <ExportActions
              onCsv={() => { void onExport(); }}
              csvBusy={csvBusy}
            />
          </div>
        </div>
        <ResponsiveTable<Payout>
          rows={payouts}
          columns={columns}
          rowKey={(p) => String(p._id)}
          emptyMessage="No payouts in this period."
        />
        {totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: 12, marginTop: 12, fontSize: 13, color: 'var(--color-tx-m)',
          }}>
            <button
              type="button" className={styles.exportBtn}
              disabled={page <= 1} onClick={() => setPage((n) => n - 1)}
            >
              Previous
            </button>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>Page {page} of {totalPages}</span>
            <button
              type="button" className={styles.exportBtn}
              disabled={page >= totalPages} onClick={() => setPage((n) => n + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Fleet Billing tab ──────────────────────────────────────────────
//
// The cross-fleet view. Every fleet surface before this was keyed on a
// single fleet id, so "which of my fleets cost what this month" meant
// opening each one in turn.
//
// Money comes from Session, not Payout — Payout carries no fleet_id, so
// it cannot be grouped by fleet at all.

function FleetBillingTab({
  period, filters, from, to,
}: {
  period: ReportPeriod;
  filters: ReportFilterParams;
  from: string;
  to: string;
}) {
  const range = periodToFromTo(period, { from, to });
  const q = useQuery({
    queryKey: ['admin', 'reports', 'fleet-billing', period === 'custom' ? `${from}..${to}` : period, filters],
    queryFn: () => fetchFleetBillingReport({ from: range.from, to: range.to }, filters),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  const columns: Column<FleetBillingRow>[] = [
    { key: 'fleet_name', header: 'Fleet', mobile: 'primary', cell: (r) => r.fleet_name },
    { key: 'session_count', header: 'Sessions', cell: (r) => r.session_count },
    { key: 'driver_count', header: 'Drivers', mobile: 'hidden', cell: (r) => r.driver_count },
    { key: 'energy_kwh', header: 'kWh', mobile: 'hidden', cell: (r) => r.energy_kwh.toFixed(2) },
    { key: 'cost_cents', header: 'Billed', cell: (r) => formatCents(r.cost_cents) },
    {
      key: 'last_session_at',
      header: 'Last session',
      mobile: 'hidden',
      cell: (r) => (r.last_session_at ? new Date(r.last_session_at).toLocaleDateString() : '—'),
    },
  ];

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard label="Fleets billed" value={String(totals?.fleet_count ?? 0)} meta={describePeriod(period)} />
        <KpiCard label="Sessions" value={String(totals?.session_count ?? 0)} meta={describePeriod(period)} />
        <KpiCard label="Energy" value={`${(totals?.energy_kwh ?? 0).toFixed(1)}`} unit="kWh" meta={describePeriod(period)} />
        <KpiCard label="Billed to fleets" value={formatCents(totals?.cost_cents ?? 0)} meta={describePeriod(period)} />
      </div>

      <div className={styles.tableCard}>
        <h3 className={styles.tableTitle}>By fleet</h3>
        {q.isLoading ? (
          <p className={styles.emptyState}>Loading…</p>
        ) : (
          <ResponsiveTable<FleetBillingRow>
            rows={rows}
            columns={columns}
            rowKey={(r) => r.fleet_id}
            emptyMessage="No fleet-billed sessions in this period. Sessions count here only when they billed to a fleet rather than to the driver."
          />
        )}
      </div>
    </div>
  );
}

// ── Customer Groups tab ────────────────────────────────────────────
//
// The same arithmetic the per-group invoice CSV and PDF already
// compute, side by side instead of one file at a time.

function CustomerGroupsTab({
  period, filters, from, to,
}: {
  period: ReportPeriod;
  filters: ReportFilterParams;
  from: string;
  to: string;
}) {
  const range = periodToFromTo(period, { from, to });
  const q = useQuery({
    queryKey: ['admin', 'reports', 'customer-groups', period === 'custom' ? `${from}..${to}` : period, filters],
    queryFn: () => fetchCustomerGroupsReport({ from: range.from, to: range.to }, filters),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  const columns: Column<CustomerGroupRow>[] = [
    { key: 'group_name', header: 'Group', mobile: 'primary', cell: (r) => r.group_name },
    { key: 'member_count', header: 'Members', cell: (r) => r.member_count },
    { key: 'active_member_count', header: 'Active', mobile: 'hidden', cell: (r) => r.active_member_count },
    { key: 'session_count', header: 'Sessions', cell: (r) => r.session_count },
    { key: 'energy_kwh', header: 'kWh', mobile: 'hidden', cell: (r) => r.energy_kwh.toFixed(2) },
    { key: 'cost_cents', header: 'Spend', cell: (r) => formatCents(r.cost_cents) },
  ];

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard label="Groups" value={String(totals?.group_count ?? 0)} meta="Including groups with no activity" />
        <KpiCard label="Sessions" value={String(totals?.session_count ?? 0)} meta="Summed per group" />
        <KpiCard label="Energy" value={`${(totals?.energy_kwh ?? 0).toFixed(1)}`} unit="kWh" meta={describePeriod(period)} />
        <KpiCard label="Spend" value={formatCents(totals?.cost_cents ?? 0)} meta="Summed per group" />
      </div>

      <div className={styles.tableCard}>
        <h3 className={styles.tableTitle}>By group</h3>
        {q.isLoading ? (
          <p className={styles.emptyState}>Loading…</p>
        ) : (
          <ResponsiveTable<CustomerGroupRow>
            rows={rows}
            columns={columns}
            rowKey={(r) => r.group_id}
            emptyMessage="No customer groups yet."
          />
        )}
        {/* A driver can belong to several groups, so a session counts
            toward each of them — exactly as each group's own invoice
            would show it. The totals are therefore "the sum of what each
            group is billed", not a deduplicated network figure. Said
            here because a reader adding the column up against Revenue
            would otherwise find it larger and have no way to know why. */}
        <p className={styles.emptyState} style={{ textAlign: 'left', marginTop: 8 }}>
          A driver in two groups counts in both, so these totals sum per group
          rather than deduplicating across the network.
        </p>
      </div>
    </div>
  );
}

// ── Uptime tab — per-charger downtime, worst first ─────────────────
//
// Deliberately NOT an uptime percentage or a trend line. Utilization
// one tab to the left already shows percent-online-now, and a trend
// restates it. What an operator acts on is which chargers are dragging
// the number down: a charger at the top of this list two periods
// running is a service call.
//
// Source is the pm.charger_offline / pm.sim_* alert intervals, which is
// the dark-charger alerting — a charger that has gone dark reports
// nothing, so its downtime cannot come from what it sent.

function UptimeTab({
  period, from, to,
}: {
  period: ReportPeriod;
  from: string;
  to: string;
}) {
  const range = periodToFromTo(period, { from, to });
  const q = useQuery({
    queryKey: ['admin', 'reports', 'downtime', period === 'custom' ? `${from}..${to}` : period],
    queryFn: () => fetchChargerDowntimeReport({ from: range.from, to: range.to, limit: 10 }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  const columns: Column<ChargerDowntimeRow>[] = [
    { key: 'charger_identity', header: 'Charger', mobile: 'primary', cell: (r) => r.charger_identity },
    { key: 'location_name', header: 'Site', mobile: 'hidden', cell: (r) => r.location_name ?? '—' },
    {
      key: 'downtime_hours',
      header: 'Downtime',
      cell: (r) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {r.downtime_hours.toFixed(1)} h{r.still_open ? ' · ongoing' : ''}
        </span>
      ),
    },
    { key: 'outage_count', header: 'Outages', cell: (r) => r.outage_count },
    { key: 'longest_outage_hours', header: 'Longest', mobile: 'hidden', cell: (r) => `${r.longest_outage_hours.toFixed(1)} h` },
    {
      key: 'last_outage_at',
      header: 'Last outage',
      mobile: 'hidden',
      cell: (r) => (r.last_outage_at ? new Date(r.last_outage_at).toLocaleDateString() : '—'),
    },
  ];

  return (
    <div className={styles.revenueBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Chargers with an outage"
          value={String(totals?.chargers_affected ?? 0)}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Total downtime"
          value={`${(totals?.downtime_hours ?? 0).toFixed(1)}`}
          unit="h"
          meta="Worst 10 listed below"
        />
        <KpiCard
          label="Still down"
          value={String(rows.filter((r) => r.still_open).length)}
          meta="Unresolved at period end"
        />
        <KpiCard
          label="Worst charger"
          value={rows[0]?.charger_identity ?? '—'}
          meta={rows[0] ? `${rows[0].downtime_hours.toFixed(1)} h` : 'No outages'}
        />
      </div>

      <div className={styles.tableCard}>
        <h3 className={styles.tableTitle}>Worst chargers by downtime</h3>
        {q.isLoading ? (
          <p className={styles.emptyState}>Loading…</p>
        ) : (
          <ResponsiveTable<ChargerDowntimeRow>
            rows={rows}
            columns={columns}
            rowKey={(r) => r.charger_id}
            emptyMessage="No charger outages recorded in this period."
          />
        )}
        <p className={styles.emptyState} style={{ textAlign: 'left', marginTop: 8 }}>
          Downtime is measured from offline alerts, clipped to the selected
          period — an outage that started before it or is still running counts
          only for the hours inside it.
        </p>
      </div>
    </div>
  );
}

// ── Coming-soon stub ───────────────────────────────────────────────

function ComingSoonCard({
  label, description,
}: { label: string; description: string }) {
  return (
    <div className={styles.comingSoon}>
      <h2>{label}</h2>
      <p>{description}</p>
      <span className={styles.comingSoonPill}>Coming soon</span>
    </div>
  );
}
